import { t } from "@lingui/macro";
import { CircleNotch, FileJs, FilePdf } from "@phosphor-icons/react";
import { buttonVariants, Card, CardContent, CardDescription, CardTitle } from "@reactive-resume/ui";
import type { ResumeDto } from "@reactive-resume/dto";
import { cn } from "@reactive-resume/utils";
import { saveAs } from "file-saver";
import { useNavigate, type NavigateFunction } from "react-router";

import { isTemporaryResume } from "@/client/constants/resume";
import { RESUME_DRAFT_KEY } from "@/client/constants/storage-keys";
import { usePrintResume } from "@/client/services/resume/print";
import { useResumeStore } from "@/client/stores/resume";

import { SectionIcon } from "../shared/section-icon";
import { DOWNLOADS_COUNT_TO_SHOW_FEEDBACK, openFeedbackModal } from "@/client/libs/tally";
import { useUser } from "@/client/services/user";
import { trackResumeDownload } from "@/client/libs/analytics";

const downloadFile = (url: string, filename: string) => {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

const onJsonExport = () => {
  const { resume } = useResumeStore.getState();
  const filename = `resumotion_resume-${resume.id}.json`;
  const resumeJSON = JSON.stringify(resume.data, null, 2);

  saveAs(new Blob([resumeJSON], { type: "application/json" }), filename);
};

const saveTemporaryResumeAndRedirect = (resume: ResumeDto, navigate: NavigateFunction) => {
  const resumeData = {
    ...resume,
    data: resume.data,
  };

  localStorage.setItem(RESUME_DRAFT_KEY, JSON.stringify(resumeData));
  navigate("/auth/register");
};

export const ExportSection = () => {
  const { printResume, loading } = usePrintResume();
  const navigate = useNavigate();
  const resume = useResumeStore((state) => state.resume);

  const isTemporary = isTemporaryResume(resume.userId);
  const { refetch } = useUser(!isTemporary);

  const onPdfExport = async () => {
    if (isTemporary) {
      saveTemporaryResumeAndRedirect(resume, navigate);
      return;
    }

    // Regular PDF export for authenticated users
    const { resume: currentResume } = useResumeStore.getState();
    const { url } = await printResume({ id: currentResume.id });
    const filename = `resumotion_resume-${currentResume.id}.pdf`;
    // Try to download using anchor for better mobile support
    downloadFile(url, filename);

    trackResumeDownload();

    const { data: user } = await refetch();
    if (user?.totalResumeDownloads === DOWNLOADS_COUNT_TO_SHOW_FEEDBACK) {
      openFeedbackModal({ layout: 'modal', width: 500, autoClose: 3000, hideTitle: true });
    }
  };

  return (
    <section id="export" className="grid gap-y-6">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-x-4">
          <SectionIcon id="export" size={18} name={t`Download`} />
          <h2 className="line-clamp-1 text-2xl font-bold lg:text-3xl">{t`Download`}</h2>
        </div>
      </header>

      <main className="grid gap-y-4">
        {!isTemporary && (
          <Card
            className={cn(
              buttonVariants({ variant: "ghost" }),
              "h-auto cursor-pointer flex-row items-center gap-x-5 px-4 pb-3 pt-1",
            )}
            onClick={onJsonExport}
          >
            <FileJs size={22} />
            <CardContent className="flex-1">
              <CardTitle className="text-sm">{t`JSON`}</CardTitle>
              <CardDescription className="font-normal">
                {t`Download a JSON snapshot of your resume. This file can be used to import your resume in the future, or can even be shared with others to collaborate.`}
              </CardDescription>
            </CardContent>
          </Card>
        )}

        <Card
          className={cn(
            buttonVariants({ variant: "ghost" }),
            "h-auto cursor-pointer flex-row items-center gap-x-5 px-4 pb-3 pt-1",
            loading && "pointer-events-none cursor-progress opacity-75",
          )}
          onClick={onPdfExport}
        >
          {loading ? <CircleNotch size={22} className="animate-spin" /> : <FilePdf size={22} />}

          <CardContent className="flex-1">
            <CardTitle className="text-sm">{t`PDF`}</CardTitle>
            <CardDescription className="font-normal">
              {isTemporary
                ? t`Save your resume and create an account to download as PDF. Your progress will be saved.`
                : t`Download a PDF of your resume. This file can be used to print your resume, send it to recruiters, or upload on job portals.`}
            </CardDescription>
          </CardContent>
        </Card>
      </main>
    </section>
  );
};
